import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { DataTableColumn, DataTableLeafColumn, SelectionState, SortDirection } from './types';
import { createColumnMenuItems } from '@/components/custom/arito/context-menu/utils';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import { Separator } from '@/components/ui/separator';
import { InputSearch } from './search-inputs';
import { getHeaderAlignClass } from './utils';
import { Checkbox } from '@/components/ui';
import { HeaderMenu } from './header-menu';
import { cn } from '@/lib/utils';

const HEADER_LABEL_HEIGHT = 30;
const HEADER_FILTER_HEIGHT = 34;

interface LeafHeaderCellProps {
  meta: DataTableLeafColumn;
  width: number;
  searchValue: string;
  sortDirection: SortDirection;
  headerHeight: number;
  rowSpan?: number;
  stickyLeft?: number;
  isLastColumn?: boolean;
  withTopBorder?: boolean;
  canResize?: boolean;
  isResizing?: boolean;
  onResizeMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  onResizeTouchStart?: React.TouchEventHandler<HTMLButtonElement>;
  onResetSize?: () => void;
  onSearch: (field: string, value: string | string[]) => void;
  onSort: (field: string, direction: SortDirection) => void;
  onHideColumn: (field: string) => void;
  disableColumnFilter?: boolean;
  selectionState: SelectionState;
  onToggleAllRows: (checked: boolean) => void;
}

const buildStickyCellStyle = (
  width: number,
  headerHeight: number,
  stickyLeft = 0,
  zIndex = 24
): React.CSSProperties => ({
  width,
  minWidth: width,
  maxWidth: width,
  height: headerHeight,
  position: 'sticky',
  left: stickyLeft,
  zIndex,
  backgroundClip: 'padding-box'
});

export function LeafHeaderCell({
  meta,
  width,
  searchValue,
  sortDirection,
  headerHeight,
  rowSpan,
  stickyLeft,
  isLastColumn,
  withTopBorder = true,
  canResize = false,
  isResizing = false,
  onResizeMouseDown,
  onResizeTouchStart,
  onResetSize,
  onSearch,
  onSort,
  onHideColumn,
  disableColumnFilter = false,
  selectionState,
  onToggleAllRows
}: LeafHeaderCellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const showFilterRow = !disableColumnFilter;
  const column = meta.column as DataTableColumn | undefined;
  const field = meta.field;
  const columnLabel = column?.headerName?.toString() || field;

  const handleSearch = useCallback(
    (value: string | string[]) => {
      onSearch(field, value);
    },
    [field, onSearch]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      if (value.includes(';')) {
        handleSearch(value.split(';').filter(Boolean));
        return;
      }

      handleSearch(value);
    },
    [handleSearch]
  );

  const menuItems = useMemo(
    () =>
      createColumnMenuItems(
        columnLabel,
        direction => onSort(field, direction),
        () => onHideColumn(field),
        undefined,
        searchValue ? () => onSearch(field, '') : undefined,
        value => handleInputChange(value),
        sortDirection
      ),
    [columnLabel, field, handleInputChange, onHideColumn, onSearch, onSort, searchValue, sortDirection]
  );

  if (meta.sticky === 'selection') {
    return (
      <th
        rowSpan={rowSpan}
        className={cn(
          withTopBorder && 'border-t',
          'border-b border-r p-0',
          TABLE_THEME.headerBorder,
          TABLE_THEME.header
        )}
        style={buildStickyCellStyle(width, headerHeight, stickyLeft, 25)}
      >
        {showFilterRow ? (
          <div className='flex h-full w-full flex-col'>
            <div className={cn('relative flex h-[30px] flex-none items-center justify-center', TABLE_THEME.header)}>
              <Checkbox
                checked={selectionState.indeterminate ? 'indeterminate' : selectionState.checked}
                className='size-4'
                onCheckedChange={checked => onToggleAllRows(Boolean(checked))}
              />
            </div>
            <Separator className={TABLE_THEME.separator} />
            <div className='h-[34px] w-full bg-[#fafafa]' />
          </div>
        ) : (
          <div className={cn('flex h-full items-center justify-center', TABLE_THEME.header)}>
            <Checkbox
              checked={selectionState.indeterminate ? 'indeterminate' : selectionState.checked}
              className='size-4'
              onCheckedChange={checked => onToggleAllRows(Boolean(checked))}
            />
          </div>
        )}
      </th>
    );
  }

  if (meta.sticky === 'index') {
    return (
      <th
        rowSpan={rowSpan}
        className={cn(
          withTopBorder && 'border-t',
          'border-b border-r p-0',
          TABLE_THEME.headerBorder,
          TABLE_THEME.header
        )}
        style={buildStickyCellStyle(width, headerHeight, stickyLeft)}
      >
        {showFilterRow ? (
          <div className='flex h-full w-full flex-col'>
            <div
              className={cn(
                'flex h-[30px] flex-none items-center justify-center text-[length:var(--erp-data-table-header-text-size)] font-semibold',
                TABLE_THEME.header,
                TABLE_THEME.headerIndexText
              )}
            >
              #
            </div>
            <Separator className={TABLE_THEME.separator} />
            <div className='h-[34px] w-full bg-[#fafafa]' />
          </div>
        ) : (
          <div
            className={cn(
              'flex h-full items-center justify-center text-[length:var(--erp-data-table-header-text-size)] font-semibold',
              TABLE_THEME.header,
              TABLE_THEME.headerIndexText
            )}
          >
            #
          </div>
        )}
      </th>
    );
  }

  const renderSearchInput = () => {
    if (column?.renderSearchInput) {
      return column.renderSearchInput({
        value: searchValue,
        onChange: value => handleSearch(value),
        onBlur: () => {},
        onKeyDown: () => {}
      });
    }

    return (
      <InputSearch
        value={searchValue}
        onChange={value => handleInputChange(value)}
        onBlur={() => {}}
        onKeyDown={() => {}}
      />
    );
  };

  return (
    <th
      rowSpan={rowSpan}
      className={cn(withTopBorder && 'border-t', 'border-b border-r p-0', TABLE_THEME.headerBorder, TABLE_THEME.header)}
      style={{ width, minWidth: width, maxWidth: width, height: headerHeight }}
    >
      <div className='relative flex h-full w-full flex-col overflow-visible'>
        <div
          ref={headerRef}
          className={cn(
            'relative m-0 flex cursor-pointer items-center p-0 text-[length:var(--erp-data-table-header-text-size)] font-semibold hover:cursor-default',
            TABLE_THEME.header,
            TABLE_THEME.headerText,
            showFilterRow ? 'h-[30px] flex-none' : 'h-full',
            getHeaderAlignClass(column?.headerAlign)
          )}
          onClick={event => {
            event.stopPropagation();

            if (!event.currentTarget.querySelector('input')?.contains(event.target as Node)) {
              setMenuOpen(true);
            }
          }}
        >
          <span className='m-0 block w-full cursor-default truncate px-3'>{columnLabel}</span>
        </div>
        {showFilterRow && (
          <>
            <Separator className={TABLE_THEME.separator} />
            <div className='flex h-[34px] w-full items-center justify-center bg-[#fafafa] p-1'>
              {renderSearchInput()}
            </div>
          </>
        )}
        {canResize && (
          <button
            type='button'
            aria-label={`Resize ${columnLabel}`}
            className={cn(
              'absolute -right-[9px] top-1/4 z-30 h-1/2 w-4 cursor-col-resize touch-none select-none bg-transparent',
              'after:absolute after:bottom-0 after:right-[9px] after:top-0 after:w-[2px] after:bg-transparent after:transition-colors',
              TABLE_THEME.resizeHandleHover,
              isResizing && TABLE_THEME.resizeHandleActive
            )}
            onClick={event => event.stopPropagation()}
            onDoubleClick={event => {
              event.stopPropagation();
              onResetSize?.();
            }}
            onMouseDown={event => {
              event.stopPropagation();
              onResizeMouseDown?.(event);
            }}
            onTouchStart={event => {
              event.stopPropagation();
              onResizeTouchStart?.(event);
            }}
          />
        )}
      </div>

      <HeaderMenu
        items={menuItems}
        anchorEl={headerRef.current}
        open={menuOpen}
        searchValue={searchValue}
        onClose={() => setMenuOpen(false)}
        onSearch={value => handleInputChange(value)}
      />
    </th>
  );
}
